import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { OrgAdminPulseData } from "@/hooks/queries/org-admin-pulse-types";

// GET /org-admin/pulse (gh-657, Screen 25) — the last seven days of activity on
// the org_admin's OWN organization, resolved server-side from the signed org_id
// claim. A post-session data call, so it rides apiFetch's silent-refresh path
// (skipAuthRefresh deliberately omitted), exactly as the dashboard does.
//
// A SEPARATE query from useOrgAdminDashboard, deliberately. The two are
// rendered by the same screen but they are not the same request: the dashboard
// is the post-sign-in hot path and the Pulse reads an append-only audit store.
// Fetching them independently is what keeps a slow or failing Pulse from taking
// the seat pool and the roster down with it — and it is what makes the panel's
// own empty / error / loading states states the screen can actually be in.

/**
 * Root key. The panel is read-only, but it is NOT invalidated by nothing: an
 * org_admin action that lands on the Pulse allow-list must refresh it, or the
 * dashboard beside it shows a change the "last 7 days" panel is still missing.
 * `useCreateOrgInvitation` invalidates this key alongside the dashboard's for
 * exactly that reason — and it has to name this key explicitly, since
 * `["org-admin","dashboard"]` is a SIBLING and prefix matching never reaches here.
 */
export const ORG_ADMIN_PULSE_QUERY_KEY = ["org-admin", "pulse"] as const;

export function useOrgAdminPulse() {
  return useQuery({
    queryKey: ORG_ADMIN_PULSE_QUERY_KEY,
    queryFn: () => apiFetch<OrgAdminPulseData>("/org-admin/pulse"),
  });
}
